import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  IconButton,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import {
  LocalShipping,
  MonetizationOn,
  CheckCircle,
  Edit,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  getInsurance,
  updateInsurance,
  createInsurance,
} from "../../redux/action/rate";

const Insurance: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [offerInsurance, setOfferInsurance] = useState("no");
  const [insuranceFee, setInsuranceFee] = useState("");

  const [editableField, setEditableField] = useState<string | null>(null);
  const [isInsuranceExists, setIsInsuranceExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [carrierId, setCarrierId] = useState<number | null>(null);

  useEffect(() => {
    const storedCarrierId = localStorage.getItem("CarrierId");
    if (storedCarrierId) {
      const carrierIdNumber = parseInt(storedCarrierId, 10);
      if (!isNaN(carrierIdNumber)) {
        setCarrierId(carrierIdNumber);
      } else {
        console.error("Invalid CarrierId in localStorage");
      }
    }
  }, []);
  useEffect(() => {
    if (carrierId) {
      dispatch<any>(getInsurance(carrierId))
        .then(unwrapResult)
        .then((res: any) => {
          if (res) {
            setIsInsuranceExists(true);
            setOfferInsurance(res.isInsurance_offered ? "yes" : "no");
            setInsuranceFee(res.insurance_fee?.toString() || "");
          } else {
            setIsInsuranceExists(false);
          }
        })
        .catch((err: any) => {
          setLoadingMessage("Failed to fetch insurance data.");
          console.error("Error fetching insurance:", err);
        });
    }
  }, [carrierId, dispatch]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setLoadingMessage("Saving changes. Please wait...");

    const insuranceData = {
      carrier_id: carrierId,
      isInsurance_offered: (offerInsurance === "yes").toString(),
      insurance_fee: offerInsurance === "yes" ? insuranceFee : "0",
    };
    try {
      let response;
      if (isInsuranceExists) {
        response = await dispatch<any>(updateInsurance(insuranceData));
      } else {
        response = await dispatch<any>(createInsurance(insuranceData));
      }
      const result = unwrapResult(response);
      setLoadingMessage("Changes saved successfully!");
      setEditableField(null);
      setIsInsuranceExists(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Typography
        sx={{
          fontWeight: "bold",
          mb: 4,
          fontSize: isMobile ? "1.5rem" : "2rem",
        }}
      >
        Insurance
      </Typography>

      <Typography
        mb={3}
        variant="subtitle2"
        fontWeight="bold"
        sx={{ mb: 1, color: theme.palette.text.secondary }}
      >
        Do you offer additional insurance above the stadard $0.60 per pound?
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel>Do you offer insurance?</InputLabel>
            <Select
              value={offerInsurance}
              onChange={(e) => setOfferInsurance(e.target.value)}
              label="Do you offer insurance?"
              disabled={isInsuranceExists && editableField !== "offerInsurance"}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
            {isInsuranceExists && editableField !== "offerInsurance" && (
              <IconButton
                onClick={() => setEditableField("offerInsurance")}
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Edit />
              </IconButton>
            )}
          </FormControl>
        </Box>

        {offerInsurance === "yes" && (
          <Box
            sx={{
              flex: "1 1 45%",
              minWidth: "240px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              label="Insurance Fee"
              variant="outlined"
              value={insuranceFee}
              onChange={(e) => setInsuranceFee(e.target.value)}
              disabled={isInsuranceExists && editableField !== "insuranceFee"}
              InputProps={{
                startAdornment: (
                  <MonetizationOn
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                ),
                endAdornment: isInsuranceExists &&
                  editableField !== "insuranceFee" && (
                    <IconButton
                      onClick={() => setEditableField("insuranceFee")}
                      sx={{ ml: 1 }}
                    >
                      <Edit />
                    </IconButton>
                  ),
              }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ px: 4, py: 2,width: isMobile ? "100%" : "200px" }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default Insurance;
